/**
 * TV1-2145 (ADR-134) — the **Blocking Gap** taxonomy, shared by server and web-app.
 *
 * A Blocking Gap is a NAMED channel-required field a Draft Publication cannot supply.
 * The classifier that produces them lives server-side (it needs the ML category
 * reference readmodel); what lives HERE is only the closed vocabulary and the rules for
 * reading it — the class list, each class's severity, how to recover a class from a
 * parameterised gap string, and how to summarise a run.
 *
 * **Why this is in the domain package rather than beside the classifier.** The review
 * surface has to group and colour gaps by severity, and the drafts list has to do the
 * same. A second, independently-maintained severity table in the web-app is precisely
 * the divergence this repo has already paid for once (ADR-077: "a second call site is
 * forbidden") and precisely what this ticket's own decision log forbids. One definition,
 * many callers.
 *
 * **Frontends must import this via the `@bett3r-dev/teselly-domain/items-publishing/constants`
 * subpath**, not the package root: these are runtime VALUES, and the root entry pulls in
 * far more than a browser bundle should carry. Types may be imported from the root.
 */

/** The closed set of gap classes. `MISSING_ATTRIBUTE` is the only parameterised one. */
export type BlockingGapClass =
  | 'NO_TITLE'
  | 'NO_DESCRIPTION'
  | 'NO_PRICE'
  | 'NO_CATEGORY'
  | 'NO_IMAGES'
  | 'MISSING_ATTRIBUTE'
  | 'DUPLICATE_WITH_IMAGES_CANDIDATE';

/**
 * A gap as it travels: a class, optionally suffixed with `:<detail>`.
 * Only `MISSING_ATTRIBUTE` carries a detail today (`MISSING_ATTRIBUTE:BRAND`), so a
 * summary can group by class while a drill-down still names the exact attribute.
 */
export type BlockingGap = string;

/** The `MISSING_ATTRIBUTE` class prefix — kept as a constant so the two sides that
 * build and parse the string can never disagree about it. */
export const MISSING_ATTRIBUTE_PREFIX = 'MISSING_ATTRIBUTE';

export type BlockingGapSeverity = 'blocking' | 'warning' | 'hint';

/**
 * Severity per class.
 *
 * `NO_DESCRIPTION` is a **warning**: every implemented connector accepts a listing with
 * no description, so treating it as a blocker would be a false blocker.
 * `DUPLICATE_WITH_IMAGES_CANDIDATE` is a **hint**: it blocks nothing, it points at the
 * manual `itemMerge` flow as the one way to recover an already-listed twin's images.
 * Everything else genuinely prevents a successful publish.
 */
export const GAP_SEVERITY: Record<BlockingGapClass, BlockingGapSeverity> = {
  NO_TITLE: 'blocking',
  NO_PRICE: 'blocking',
  NO_CATEGORY: 'blocking',
  NO_IMAGES: 'blocking',
  MISSING_ATTRIBUTE: 'blocking',
  NO_DESCRIPTION: 'warning',
  DUPLICATE_WITH_IMAGES_CANDIDATE: 'hint'
};

/** Recover the CLASS from a possibly-parameterised gap (`MISSING_ATTRIBUTE:BRAND` →
 * `MISSING_ATTRIBUTE`). */
export const gapClassOf = ( gap: BlockingGap ): string => gap.split( ':' )[ 0 ];

/** The detail after the colon, when there is one (`BRAND`), else undefined. */
export const gapDetailOf = ( gap: BlockingGap ): string | undefined => {
  const index = gap.indexOf( ':' );
  return index === -1 ? undefined : gap.slice( index + 1 ) || undefined;
};

/** Does this gap set actually prevent a publish? Warnings and hints do not. */
export const isDraftReadyToPublish = ( gaps: BlockingGap[]): boolean =>
  !gaps.some(( gap ) => GAP_SEVERITY[ gapClassOf( gap ) as BlockingGapClass ] === 'blocking' );

/**
 * Counts by gap CLASS across a run — the shape both the review summary and the drafts
 * list consume.
 *
 * Each class is counted **once per row**, however many attributes that row is missing.
 * Otherwise one row missing nine attributes would dominate the summary and misrepresent
 * how many DRAFTS are affected, which is the number the operator is deciding on. The
 * counts therefore do not sum to the row total — a row with three kinds of gap
 * contributes to three buckets.
 */
export const summarizeGapClasses = ( perRow: BlockingGap[][]): Record<string, number> => {
  const counts: Record<string, number> = {};
  for ( const gaps of perRow ) {
    for ( const gapClass of new Set( gaps.map( gapClassOf ))) {
      counts[ gapClass ] = ( counts[ gapClass ] || 0 ) + 1;
    }
  }
  return counts;
};
